import { Box, Button, Flex, Grid, Spinner } from '@chakra-ui/react';
import { Stage } from '@react-three/drei';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Stages } from 'src/providers/Cartridge/cartridgeProvider';
import { Color } from 'src/theme/color';

const FinalizationButton = ({
	isLoading,
	isActive,
	disabled,
	onClick,
	children,
	grayedOut
}: {
	isLoading: boolean;
	isActive: boolean;
	disabled: boolean;
	grayedOut: boolean;
	onClick: () => void;
	children: React.ReactNode;
}) => {
	return (
		<Flex
			h="73px"
			bg={grayedOut ? Color. GRAY_2 : Color.HASHUP}
			onClick={onClick}
			cursor={disabled ? 'default' : 'pointer'}
			borderRadius="4px"
			alignItems="center"
			opacity={disabled && !isLoading ? '0.5' : '1'}
			justify="center"
		>
			{isLoading ? <Spinner /> : children}
		</Flex>
	);
};

const ProgressLine = ({ loading, finished }: { loading: boolean; finished: boolean }) => {
	const loadingPercent = useMemo(() => {
		if (finished) return '100%';
		if (loading) return '50%';
		else return '0%';
	}, [loading, finished]);

	return (
		<Box
			h="5px"
			background={`repeating-linear-gradient(to right, ${Color.GRAY_2} 0px, ${Color.GRAY_2} 2px, transparent 2px, transparent 6px)`}
			w="100%"
			position="relative"
			opacity={finished ? '0.5' : '1'}
		>
			<Box
				h="100%"
				bg={Color.HASHUP}
				w={loadingPercent}
				transition="0.5s width"
			></Box>
		</Box>
	);
};

export const FinalizationButtons = ({
	stage,
	handleDeploy,
	handleAllowance,
	handleSetForSale,
}: {
	stage: Stages;
	handleDeploy: () => void;
	handleAllowance: () => void;
	handleSetForSale: () => void;
	}) => {
	const { t } = useTranslation();

	return (
		<Grid
			templateColumns="1fr 60px 1fr 60px 1fr"
			alignItems="center"
		>
			<FinalizationButton
				disabled={stage > Stages.METADATA_UPLOAD}
				isActive={stage >= Stages.UNDEPLOYED && stage < Stages.DEPLOYED}
				isLoading={stage >= Stages.METADATA_UPLOAD && stage < Stages.DEPLOYED}
				onClick={handleDeploy}
				grayedOut={false}
			>
				{t('form.steps.finalization.deploy')}
			</FinalizationButton>
			<ProgressLine
				loading={stage > Stages.UNDEPLOYED && stage < Stages.DEPLOYED}
				finished={stage >= Stages.DEPLOYED}
				
			/>
			<FinalizationButton
				disabled={stage < Stages.DEPLOYED || stage > Stages.ALLOWING}
				isActive={stage >= Stages.UNDEPLOYED && stage < Stages.DEPLOYED}
				isLoading={stage >= Stages.ALLOWING && stage < Stages.IGO_ALLOWED}
				onClick={handleAllowance}
				grayedOut={stage < Stages.DEPLOYED}
			>
				{t('form.steps.finalization.approve')}
			</FinalizationButton>
			<ProgressLine
				loading={stage > Stages.DEPLOYED && stage < Stages.IGO_ALLOWED}
				finished={stage >= Stages.IGO_ALLOWED}
			/>
			<FinalizationButton
				disabled={stage != Stages.IGO_ALLOWED}
				isActive={stage >= Stages.UNDEPLOYED && stage < Stages.DEPLOYED}
				isLoading={stage > Stages.IGO_ALLOWED && stage < Stages.IGO_SET}
				onClick={handleSetForSale}
				grayedOut={stage < Stages.IGO_ALLOWED}
			>
				{t('form.steps.finalization.send')}
			</FinalizationButton>
		</Grid>
	);
};
